import {
    call,
    CallEffect,
    put,
    PutEffect,
    select,
    SelectEffect,
    take,
    TakeEffect,
    takeLatest,
} from "redux-saga/effects";
import {
    EditorType,
    ElementsValues,
    ElementsValuesMap,
    ElementsValuesType,
    FieldValidationError,
    fileSign,
    fileSignHandler,
    getElementsValues,
    getToken,
    GetTokenGeneratorType,
    getValidationErrors,
    HttpMethod,
    safeSaga,
    SignS3ResourceResponse,
    SignTokenType,
    TokenType,
    updateJobElementsValues,
} from "@data-force/app";
import {all, AllEffect, ForkEffect} from "@redux-saga/core/effects";
import {ValidationErrorsMap} from "@data-force/app/interfaces/store/reducers/schema";
import {getTranslate, LocalizeState, TranslateFunction} from "react-localize-redux";
import {getFormErrors} from "@data-force/app/utils/common";
import {setJobElementErrors, setTaskElementErrors} from "@data-force/app/store/actions/schema";
import axios, {AxiosResponse} from "axios";
import Nullable from "@data-force/app/interfaces/nullable";
import {
    GetBatchFieldResponse,
    JobDetails,
    JobPhase,
    RetrieveElementsValuesPayload,
} from "../../interfaces/store/actions/payload/job";
import HistoryParams from "../../interfaces/historyParams";
import {
    getHistoryParams,
    getUpdatedElementMetadata,
    isJobExpired,
    isTaskChangeable,
    isTaskFinished,
} from "../../utils/common";
import StoreState from "../../interfaces/store/storeState";
import {
    RETRIEVE_ELEMENTS_VALUES,
    SAVE_BATCH_FIELDS_DATA,
    SAVE_TASK_SUCCESS,
    SIGN_FILE_RESOURCE,
    SIGN_JOB_FILE,
    SIGN_TASK_FILE,
} from "../actions/availableActions";
import {MetadataValidationInfo} from "../../interfaces/store/sagas/task";
import {saveActiveTask, saveCompleted, updateActiveTaskPrivates} from "../actions/task";
import {
    GetTaskResponse, Task, TaskEntity,
} from "../../interfaces/store/actions/payload/task";
import {addAlerts} from "../actions/layout";
import {
    SignFileResourceAction,
    SignJobFileAction,
    SignTaskFileAction,
} from "../../interfaces/store/actions/uploadFiles";
import {signJobFile, signTaskFile} from "../actions/uploadFiles";
import {JobState} from "../../interfaces/store/reducers/jobState";

export function getAnnotationUrl(): SelectEffect {
    return select((state: StoreState): string => state.serviceProperties.annotationEditorUrl);
}

export function getFluxUrl(): SelectEffect {
    return select((state: StoreState): string => state.serviceProperties.fluxUrl);
}

export type RetrieveElementsValuesGenerator = Generator<SelectEffect,
ElementsValues,
string | ElementsValuesMap>;

export function* retrieveElementsValues(payload: RetrieveElementsValuesPayload): RetrieveElementsValuesGenerator {
    const {metadataId, elementsValuesType}: RetrieveElementsValuesPayload = payload;
    const {phase}: HistoryParams = getHistoryParams();

    const schemaKey: string = (
        yield select(({project: {projectDetails}}: StoreState): string => (
            `${projectDetails?.schemaId}_${phase}`
        ))
    ) as string;

    const elementsValuesMap: ElementsValuesMap = (
        yield select((state: StoreState): ElementsValuesMap => state.schema.elementsValuesMap)
    ) as ElementsValuesMap;

    return getElementsValues({
        elementsValuesMap,
        schemaKey,
        metadataId,
        elementsValuesType,
    });
}

export type IsValidMetadataGenerator = Generator<SelectEffect, boolean, string | ValidationErrorsMap>;

export function* isValidMetadata(metadataValidationInfo: MetadataValidationInfo): IsValidMetadataGenerator {
    const {metadataId, elementsValuesType}: MetadataValidationInfo = metadataValidationInfo;
    const {phase}: HistoryParams = getHistoryParams();

    const schemaKey: string = (
        yield select(({project: {projectDetails}}: StoreState): string => (
            `${projectDetails?.schemaId}_${phase}`
        ))
    ) as string;

    const validationErrorsMap: ValidationErrorsMap = (
        yield select((state: StoreState): ValidationErrorsMap => state.schema.validationErrorsMap)
    ) as ValidationErrorsMap;

    const metadataErrors: FieldValidationError[] = getValidationErrors({
        validationErrorsMap,
        schemaKey,
        metadataId,
        elementsValuesType,
    });

    return !metadataErrors.length;
}

export type MetadataErrorsGenerator = Generator<SelectEffect
| CallEffect
| PutEffect,
boolean,
string | ElementsValues | EditorType | LocalizeState>;

export function* metadataHasErrors(metadataValidationInfo: MetadataValidationInfo): MetadataErrorsGenerator {
    const {metadataId, elementsValuesType}: MetadataValidationInfo = metadataValidationInfo;
    const historyParams: Nullable<HistoryParams> = getHistoryParams();

    const schemaKey: string = (
        yield select(({project: {projectDetails}}: StoreState): string => (
            `${projectDetails?.schemaId}_${historyParams?.phase}`
        ))
    ) as string;

    const editor: EditorType = (
        yield select(({schema: {parsedSchemas}}: StoreState): EditorType => parsedSchemas[schemaKey] as EditorType)
    ) as EditorType;

    const metadata: ElementsValues = (
        yield call(retrieveElementsValues, {
            metadataId,
            elementsValuesType,
        })
    ) as ElementsValues;

    const localize: LocalizeState = (yield select(
        (state: StoreState): LocalizeState => state.localize,
    )) as LocalizeState;
    const translate: TranslateFunction = getTranslate(localize);
    const taskReferences: string[] = editor?.spanReferences || [];
    const jobReferences: string[] = editor?.batchFieldReferences || [];
    const references: string[] = elementsValuesType === ElementsValuesType.TASK ? taskReferences : jobReferences;

    const validationErrors: FieldValidationError[] = getFormErrors(
        metadata,
        editor,
        references,
        schemaKey,
        translate,
    );

    if (elementsValuesType === ElementsValuesType.TASK) {
        yield put(setTaskElementErrors({schemaKey, metadataId, validationErrors}));
    } else {
        yield put(setJobElementErrors({schemaKey, metadataId, validationErrors}));
    }

    return !validationErrors.length;
}

export type GetJobMetadataGenerator = Generator<SelectEffect
| Promise<AxiosResponse>
| GetTokenGeneratorType,
ElementsValues,
string | GetBatchFieldResponse>;

export function* getJobMetadata(): GetJobMetadataGenerator {
    const annotationUrl: string = (yield getAnnotationUrl()) as string;
    const {projectId, jobId, phase}: HistoryParams = getHistoryParams();

    const jobToken: string = (yield getToken({
        projectId, jobId, phase, tokenType: TokenType.JOB,
    })) as string;

    const {data: {data: jobMetadata}}: GetBatchFieldResponse = (
        yield axios.get(`${annotationUrl}/batch?token=${jobToken}`)
    ) as GetBatchFieldResponse;

    return jobMetadata;
}

export type GetTaskMetadataGenerator = Generator<SelectEffect
| Promise<AxiosResponse<GetTaskResponse>>
| GetTokenGeneratorType,
Nullable<ElementsValues>,
string | AxiosResponse<GetTaskResponse>>;

export function* getTaskMetadata(id: string): GetTaskMetadataGenerator {
    const annotationUrl: string = (yield getAnnotationUrl()) as string;
    const {projectId, jobId, phase}: HistoryParams = getHistoryParams();

    const taskToken: string = (yield getToken({
        projectId,
        jobId,
        taskId: id,
        phase,
    })) as string;

    const {data: {data: taskMetadata}}: AxiosResponse<GetTaskResponse> = (yield axios.get(
        `${annotationUrl}/task?token=${taskToken}`,
    )) as AxiosResponse<GetTaskResponse>;

    return taskMetadata;
}

/**
 * Resolves current schema EditorType
 *
 * @param {phase} string
 * @return {EditorType}
 */
type ResolveEditorElementType = Generator<SelectEffect, EditorType>;
export function* resolveEditorElement(phase: string = ""): ResolveEditorElementType {
    const editor: EditorType = (yield select(
        ({schema: {parsedSchemas}, project: {projectDetails}}: StoreState): EditorType => (
            parsedSchemas[`${projectDetails?.schemaId}_${phase}`] as EditorType
        ),
    )) as EditorType;
    return editor;
}

export type SaveActiveTaskGenerator = Generator<SelectEffect
| PutEffect
| TakeEffect,
Nullable<Task>,
Nullable<Task> | TaskEntity[] | JobPhase>;

export function* saveAndReturnActiveTask(isSubmitAfterExpiry: boolean): SaveActiveTaskGenerator {
    let activeTask: Nullable<Task> = (
        yield select((state: StoreState): Nullable<Task> => state.job.activeTask)
    ) as Task;
    if (activeTask) {
        const taskList: TaskEntity[] = (
            yield select((state: StoreState): TaskEntity[] => state.job.tasks)
        ) as TaskEntity[];
        const {status}: TaskEntity = taskList.find(
            (entity: TaskEntity): boolean => entity.payload === activeTask!.id,
        )!;
        const {accessStatus, status: phaseStatus}: JobPhase = (
            yield select((state: StoreState): JobPhase => state.job.jobPhase)
        ) as JobPhase;
        const jobExpired: boolean = isJobExpired({accessStatus, phaseStatus}) && !isSubmitAfterExpiry;

        if (isTaskChangeable(status) && !jobExpired) {
            yield put(saveActiveTask(activeTask));
            yield take(SAVE_TASK_SUCCESS);
            activeTask = (
                yield select((state: StoreState): Nullable<Task> => state.job.activeTask)
            ) as Task;
        } else {
            activeTask = {
                ...activeTask,
                valid: true,
            };
        }
    }

    return activeTask;
}

export type SaveBatchFieldsDataGenerator = Generator<SelectEffect
| PutEffect
| CallEffect
| Promise<AxiosResponse<{}>>
| GetTokenGeneratorType
| CallEffect<ElementsValues | GetJobMetadataGenerator | GetTaskMetadataGenerator | EditorType>
| AllEffect<GetTokenGeneratorType>,
void,
string | ElementsValues | EditorType | Nullable<AxiosResponse<{}>> | JobState | Task>;

export function* saveBatchFieldsData(isSubmitAfterExpiry: boolean): SaveBatchFieldsDataGenerator {
    const {
        tasks: taskList,
        activeTask,
        jobPhase: {accessStatus, status: phaseStatus},
    }: JobState = (
        yield select((state: StoreState): JobState => state.job)
    ) as JobState;
    const {projectId, jobId, phase}: HistoryParams = getHistoryParams();
    const editor: EditorType = (
        yield call(resolveEditorElement, phase)
    ) as EditorType;

    const isReadOnlyJob: boolean = taskList.some((task: TaskEntity) => isTaskFinished(task.status));
    const jobExpired: boolean = isJobExpired({accessStatus, phaseStatus}) && !isSubmitAfterExpiry;

    if (!isReadOnlyJob && !jobExpired) {
        yield put(updateActiveTaskPrivates({saveInProgress: true}));
        const annotationUrl: string = (yield getAnnotationUrl()) as string;

        const jobToken: string = (yield getToken({
            projectId, jobId, phase, tokenType: TokenType.JOB,
        })) as string;

        const jobMetadataToSave: ElementsValues = (
            yield call(retrieveElementsValues, {
                metadataId: jobId,
                elementsValuesType: ElementsValuesType.JOB,
            })
        ) as ElementsValues;

        const response: Nullable<AxiosResponse<{}>> = ((yield axios.put(
            `${annotationUrl}/batch/update`, {
                token: jobToken,
                data: jobMetadataToSave,
            },
        ))) as Nullable<AxiosResponse<{}>>;

        if (response) {
            const jobMetadata: ElementsValues = (yield call(getJobMetadata)) as ElementsValues;

            const updatedJobMetadata: ElementsValues = getUpdatedElementMetadata({
                editor,
                current: jobMetadataToSave,
                next: jobMetadata,
                elementsValuesType: ElementsValuesType.JOB,
            }) as ElementsValues;

            const schemaKey: string = (
                yield select(({project: {projectDetails}}: StoreState): string => (
                    `${projectDetails?.schemaId}_${phase}`
                ))
            ) as string;
            yield put(updateJobElementsValues({
                schemaKey,
                metadataId: jobId,
                values: updatedJobMetadata,
            }));

            if (activeTask) {
                yield call(saveAndReturnActiveTask, isSubmitAfterExpiry);
            } else {
                yield put(updateActiveTaskPrivates({saveInProgress: false}));
                yield put(saveCompleted());
            }
        } else {
            yield put(updateActiveTaskPrivates({saveInProgress: false}));
        }
    }
}

export type HandleValidationErrorGenerator = Generator<SelectEffect
| PutEffect,
void,
LocalizeState>;

export function* handleValidationError(taskId: string): HandleValidationErrorGenerator {
    if (window.opener) {
        window.opener.postMessage({event: "CHANGE_TASK"}, window.origin);// when testing locally change window.origin to 'http://localhost:8080/'
    }
    const localize: LocalizeState = (yield select(
        (state: StoreState): LocalizeState => state.localize,
    )) as LocalizeState;
    const translate: TranslateFunction = getTranslate(localize);

    yield put(addAlerts([{
        content: translate("components.Footer.Footer.invalidTask.text"),
        duration: 0,
        type: "error",
        key: `${taskId}-invalidTask`,
    }]));
}

export type SignTaskFileGeneratorType = Generator<SelectEffect
| CallEffect
| GetTokenGeneratorType
| Promise<AxiosResponse<SignS3ResourceResponse>>,
SignS3ResourceResponse,
string | SignS3ResourceResponse>;

export function* signTaskFileHandler({
    payload: {taskEntity, historyParams, fieldItems},
}: SignTaskFileAction): SignTaskFileGeneratorType {
    if (!taskEntity) {
        throw Error("File sign is not possible!");
    }

    const annotationUrl: string = (yield getAnnotationUrl()) as string;
    const {
        id: fluxTaskId, projectId, jobId, payload: annotationTaskId,
    }: TaskEntity = taskEntity;
    const {phase}: HistoryParams = historyParams;

    const taskToken: string = (yield getToken({
        projectId, jobId, taskId: fluxTaskId, phase,
    })) as string;

    return (yield call(fileSignHandler, fileSign({
        baseUrl: annotationUrl,
        entityId: annotationTaskId,
        fieldItems,
        tokenType: SignTokenType.TASK,
        taskTokens: [{token: taskToken}],
    }))) as SignS3ResourceResponse;
}

export type SignJobResourceGeneratorType = Generator<SelectEffect
| CallEffect
| GetTokenGeneratorType
| Promise<AxiosResponse<SignS3ResourceResponse>>,
SignS3ResourceResponse,
string | SignS3ResourceResponse>;

export function* signJobFileHandler({
    payload: {
        fieldItems,
        historyParams: {projectId, jobId, phase},
        batchId,
    },
}: SignJobFileAction): SignJobResourceGeneratorType {
    const annotationUrl: string = (yield getAnnotationUrl()) as string;
    const jobToken: string = (yield getToken({
        projectId, jobId, phase, tokenType: TokenType.JOB,
    })) as string;

    return (yield call(fileSignHandler, fileSign({
        baseUrl: annotationUrl,
        entityId: batchId,
        fieldItems,
        tokenType: SignTokenType.BATCH,
        batchTokens: [{token: jobToken}],
    }))) as SignS3ResourceResponse;
}

export type SignFileResourceGeneratorType = Generator<SelectEffect
| CallEffect
| GetTokenGeneratorType
| Promise<AxiosResponse<SignS3ResourceResponse>>,
SignS3ResourceResponse | undefined,
string | SignS3ResourceResponse | Nullable<Task> | TaskEntity[] | Nullable<JobDetails>>;

export function* signFileResourceHandler({
    payload: {fileReference, signType},
}: SignFileResourceAction): SignFileResourceGeneratorType {
    let response: SignS3ResourceResponse | undefined;
    switch (signType) {
        case SignTokenType.TASK:
        case SignTokenType.SEGMENT:
        case SignTokenType.RELATION:
        case SignTokenType.FRAGMENT: {
            const activeTask: Nullable<Task> = (
                yield select((state: StoreState): Nullable<Task> => state.job.activeTask)
            ) as Nullable<Task>;
            if (!activeTask) {
                throw Error("Sign not possible, no task");
            }
            const {id: taskId}: Task = activeTask;
            const taskList: TaskEntity[] = (
                yield select((state: StoreState): TaskEntity[] => state.job.tasks)
            ) as TaskEntity[];
            const taskEntity: Nullable<TaskEntity> = taskList.find(
                (currentTaskEntity: TaskEntity): boolean => (taskId === currentTaskEntity.payload),
            );

            response = (yield call(signTaskFileHandler, signTaskFile({
                historyParams: getHistoryParams(),
                taskEntity,
                fieldItems: [{
                    fileField: fileReference,
                    methods: [HttpMethod.GET, HttpMethod.HEAD],
                }],
            }))) as SignS3ResourceResponse;
            break;
        }
        case SignTokenType.BATCH: {
            const jobDetails: Nullable<JobDetails> = (
                yield select((state: StoreState): Nullable<JobDetails> => state.job.jobDetails)
            ) as Nullable<JobDetails>;
            if (!jobDetails) {
                throw Error("Sign not possible, no job");
            }
            response = (yield call(signJobFileHandler, signJobFile({
                historyParams: getHistoryParams(),
                fieldItems: [{
                    fileField: fileReference,
                    methods: [HttpMethod.GET, HttpMethod.HEAD],
                }],
                batchId: jobDetails.batchId,
            }))) as SignS3ResourceResponse;
            break;
        }
        default:
            break;
    }

    return response;
}

export function* actionWatcher(): IterableIterator<AllEffect<ForkEffect>> {
    yield all([
        takeLatest(RETRIEVE_ELEMENTS_VALUES, safeSaga(retrieveElementsValues)),
        takeLatest(SAVE_BATCH_FIELDS_DATA, safeSaga(saveBatchFieldsData)),
        takeLatest(SIGN_TASK_FILE, safeSaga(signTaskFileHandler)),
        takeLatest(SIGN_JOB_FILE, safeSaga(signJobFileHandler)),
        takeLatest(SIGN_FILE_RESOURCE, safeSaga(signFileResourceHandler)),
    ]);
}
